import { useEffect } from "react";
import { XIcon } from "~/components/primitives/Icons";
import { Button } from "~/components/primitives/Button";
import { Wordmark } from "./Wordmark";
import { cn } from "~/lib/cn";

export function MobileNav({
  open,
  onClose,
  items,
  contactHref,
}: {
  open: boolean;
  onClose: () => void;
  items: Array<{ label: string; href: string }>;
  contactHref: string;
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <div
      aria-hidden={!open}
      className={cn(
        "fixed inset-0 z-[60] transition-opacity duration-[var(--duration-standard)] ease-[var(--ease-prism)]",
        open
          ? "pointer-events-auto opacity-100"
          : "pointer-events-none opacity-0",
      )}
    >
      <div
        className="absolute inset-0 bg-glacial-navy/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className={cn(
          "glass-dark relative flex h-full w-full flex-col px-6 pt-6 pb-[max(24px,env(safe-area-inset-bottom))] transition-transform duration-[var(--duration-cinematic)] ease-[var(--ease-prism)]",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center justify-between pt-[max(12px,env(safe-area-inset-top))]">
          <Wordmark onDark />
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-[var(--radius-btn)] text-[color:var(--on-navy)]"
            aria-label="Close menu"
            onClick={onClose}
          >
            <XIcon size={20} aria-hidden="true" />
          </button>
        </div>
        <nav className="mt-16 flex flex-col gap-4" aria-label="Primary">
          {items.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={onClose}
              className="font-display text-3xl font-medium tracking-[-0.015em] text-[color:var(--on-navy)]"
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-3">
          <Button
            href={contactHref}
            variant="dark-glass"
            className="w-full"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
          >
            Request an invite
          </Button>
          <p className="text-center text-xs text-[color:rgba(245,247,250,0.6)]">
            Jotty is invite-only. Contact Kugie to try it.
          </p>
        </div>
      </div>
    </div>
  );
}
